import { differenceInDays } from 'date-fns';
import React, { useState } from 'react';
import { usePopper } from 'react-popper';
import styled from 'styled-components';
import { promptName, useObserverReturningStore } from '../../CodesThatsGottaGoSomewhereElse';
import { AccountPlanLiteData, AccountTrialLiteData } from '../../api/endpoints/me';
import { Icon } from '../../concepts/resources/Icon';
import { BetterDropButton } from '../../ui/DropButton';
import { LoadingBar } from '../../ui/LoadingBar';
import { Roundings } from '../../ui/Standards';
import { useResolve } from '../Container';
import { BoardInfo } from '../canvas/BoardInfo';
import { EditorSession } from '../canvas/EditorSession';
import { PopperInner, PopperMount } from '../canvas/ui/PopperElements';
import { Page } from '../common/Page';
import { ShowIfHasPermission } from '../common/PermissionElements';
import { ButtonStrip, Copy, Em, Flexer, Spacer, Title } from '../dashboard/elements/LayoutElements';
import { UpgradeButton } from '../upgrade/UpgradeButton';
import { Colors, Float, Prompt } from './../../ui';
import { AccountModel } from './AccountModel';
import { BoardUsageModel } from './BoardUsageModel';
import { EmailVerifyHeader } from './EmailVerifyHeader';

const Text = styled.div<{ color: string }>`
    margin-bottom: .25em;
    font-size: .875rem;
    text-align: right;
    color: ${p => p.color};
`;

const FreePlanBoardUsageView = React.memo(() => {

    const usage = useObserverReturningStore(useResolve(BoardUsageModel));
    const color = usage.remaining > 0 ? Colors.primaryText : Colors.negative;

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-end',
    });

    const [open, setOpen] = useState(false);
    const onToggle = () => setOpen(x => !x);
    const pn = promptName();

    if (!usage.enabled) return null;

    return <Flexer flex="row" data-tour="usage">
        <BetterDropButton ref={setRefElmt} className={`ignore-${pn}`} active={open} padding="0 .5" onClick={onToggle}>
            <Flexer padding=".25" flex="row">
                <Flexer width="12" flex center="justify">
                    <Text color={color}>{usage.remaining} boards remaining</Text>
                    <LoadingBar progress={usage.used / usage.limit} color={color} />
                </Flexer>
            </Flexer>
        </BetterDropButton>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => setOpen(false)}>
                <PopperInner direction="down">
                    <Float>
                        <Flexer width="36" padding="1">
                            <Title>You have {usage.remaining} boards remaining!</Title>
                            <Copy flush>
                                Teams on the Free Plan are limited to {usage.limit} active boards. For unlimited boards,
                                plus access to more features, upgrade to one of our premium plans!
                            </Copy>
                            <Spacer f={1} />
                            <ButtonStrip>
                                <UpgradeButton />
                            </ButtonStrip>
                        </Flexer>
                    </Float>
                </PopperInner>
            </Prompt>
        </PopperMount>}
    </Flexer>;
});

const FreeTrialProgressView = React.memo<{ plan: AccountPlanLiteData, trial: AccountTrialLiteData }>(({ plan, trial }) => {
    const now = new Date();
    const remaining = differenceInDays(trial.expiresAt, now);

    const [refElmt, setRefElmt] = useState(null);
    const [popElmt, setPopElmt] = useState(null);

    const { styles, attributes } = usePopper(refElmt, popElmt, {
        placement: 'bottom-end',
    });

    const [open, setOpen] = useState(false);
    const onToggle = () => setOpen(x => !x);
    const pn = promptName();

    return <Flexer flex="row">
        <BetterDropButton ref={setRefElmt} className={`ignore-${pn}`} active={open} padding="0 .5" onClick={onToggle}>
            <Flexer flex center="justify">
                <Title size="sm" flush whiteSpace="nowrap">{Math.max(0, remaining)} days remaining</Title>
                <Copy size=".875" flush whiteSpace="nowrap">of your free trial</Copy>
            </Flexer>
        </BetterDropButton>
        {open && <PopperMount ref={setPopElmt} style={styles.popper} {...attributes.popper}>
            <Prompt name={pn} ignoreEnter ignoreEscape onAction={() => setOpen(false)}>
                <PopperInner direction="down">
                    <Float>
                        <Flexer width="24" padding="1">
                            <Title>You are currently on a Free Trial</Title>
                            <Copy flush>
                                Your trial will end on <Em>{trial.expiresAt.toLocaleDateString()}</Em>.
                            </Copy>
                            <Spacer f={1} />
                            <ButtonStrip>
                                <UpgradeButton />
                            </ButtonStrip>
                        </Flexer>
                    </Float>
                </PopperInner>
            </Prompt>
        </PopperMount>}
    </Flexer>;
});

export const PlanStateHeaderBlock = () => {
    const { plan, trial } = useResolve(AccountModel);
    return <ShowIfHasPermission requires={['da:write', 'bo:write']}>
        <Page.HeaderBlock>
            <EmailVerifyHeader />
            {!!trial && plan.name.includes('trial') && <FreeTrialProgressView plan={plan} trial={trial} />}
        </Page.HeaderBlock>
    </ShowIfHasPermission>;
};

const Message = styled.div`
    display: flex;
    align-items: center;
    gap: .5rem;
    background: ${Colors.hover};
    padding: .5rem 1rem;
    border-radius: ${Roundings.md};
`;

export const BoardLimitHeaderBlock = () => {
    const usage = useObserverReturningStore(useResolve(BoardUsageModel));
    if (!usage.enabled || usage.remaining > 0) return null;
    const board = useResolve(EditorSession).get(BoardInfo);
    if (usage.isBoardActive(board.id)) return null;

    return <Page.HeaderBlock>
        <Flexer flex="row" gap="1">
            <Message>
                <Icon size="1.25" src="deqo://icons/hero/exclamation-circle" />
                <span>View-only: Your team has reached it's free active board limit.</span>
            </Message>
            <ShowIfHasPermission requires="da:write">
                <UpgradeButton />
            </ShowIfHasPermission>
        </Flexer>
    </Page.HeaderBlock>;
};
